import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { CaptchaRejectionService } from '@ui/tenant-pool/shared/friendly-captcha/data-access';
import { Store } from '@ngrx/store';

import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';

import {
  errorMessageParser,
  Go,
  ModalService,
  SessionStorageService,
  ShowError,
  ShowInfo
} from '@ui/legacy-lib';
import { GuestApplication } from '@ui/shared/model';
import { isCaptchaResponseInvalidError } from '@ui/tenant-pool/shared/util';
import { UserData } from '@ui/tenant-pool/shared/user/model';
import { GuestModeFacade } from '../../core/services/guest-mode.facade';
import { GuestRegisterModalComponent } from '../../components/guest-register-modal/guest-register-modal.component';
import { MainPageNavigation } from '../../config/navigation.config';
import { notificationConfig } from '../../config/notification.config';
import {
  ACCEPT_GUEST_APPOINTMENT,
  AcceptGuestAppointment,
  AcceptGuestAppointmentFail,
  AcceptGuestAppointmentSuccess,
  CANCEL_GUEST_APPOINTMENT,
  CancelGuestAppointment,
  CancelGuestAppointmentFail,
  CancelGuestAppointmentSuccess,
  DELETE_GUEST,
  DeleteGuest,
  DeleteGuestFail,
  DeleteGuestSuccess,
  GUEST_APPLICATION_REGISTER,
  GUEST_DECLARE_INTENT,
  GUEST_REGISTER,
  GuestApplicationRegister,
  GuestDeclareIntent,
  GuestDeclareIntentFail,
  GuestDeclareIntentSuccess,
  GuestRegister,
  GuestRegisterFail,
  LOAD_GUEST_APPLICATION,
  LOAD_GUEST_APPOINTMENTS,
  LOAD_GUEST_BRANDING,
  LOAD_GUEST_CUSTOM_QUESTION_RESPONSES,
  LOAD_GUEST_HIERARCHICAL_QUESTION_RESPONSES,
  LoadGuestApplication,
  LoadGuestApplicationFail,
  LoadGuestApplicationSuccess,
  LoadGuestAppointments,
  LoadGuestAppointmentsFail,
  LoadGuestAppointmentsSuccess,
  LoadGuestBranding,
  LoadGuestBrandingFail,
  LoadGuestBrandingSuccess,
  LoadGuestCustomQuestionResponses,
  LoadGuestCustomQuestionResponsesFail,
  LoadGuestCustomQuestionResponsesSuccess,
  LoadGuestHierarchicalQuestionsResponses,
  LoadGuestHierarchicalQuestionsResponsesFail,
  LoadGuestHierarchicalQuestionsResponsesSuccess,
  OPEN_GUEST_REGISTER_MODAL,
  OpenGuestRegisterModal
} from './guest-mode.actions';

@Injectable()
export class GuestModeEffects {
  private actions$ = inject(Actions);
  private facade = inject(GuestModeFacade);
  /**
   * Register after guest application or as prerequisite in order to declare intent
   */

  guestRegister$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GuestRegister>(GUEST_REGISTER),
      switchMap(
        ({
          userData,
          token,
          applicationIdToDeclareIntentTo,
          captchaResponse
        }) =>
          this.facade.register(userData, token, captchaResponse).pipe(
            mergeMap(() =>
              this.registerSuccess(userData, applicationIdToDeclareIntentTo)
            ),
            catchError(err => {
              if (isCaptchaResponseInvalidError(err)) {
                return [
                  new GuestRegisterFail(err),
                  new ShowError(
                    notificationConfig.guest.register.captchaInvalid
                  )
                ];
              }
              return [
                new GuestRegisterFail(err),
                new ShowError(notificationConfig.guest.register.error)
              ];
            })
          )
      )
    )
  );
  /**
   * Register at the end of guest apply.
   */

  guestApplicationRegister$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GuestApplicationRegister>(GUEST_APPLICATION_REGISTER),
      switchMap(({ userData, token, captchaResponse }) => {
        return this.facade
          .applicationRegister(userData, token, captchaResponse)
          .pipe(
            mergeMap(() => this.registerSuccess(userData)),
            catchError(err => {
              // The widget the guest solved belongs to this page, so the
              // rejection goes to the service every captcha surface reads.
              if (this.captcha.reject(err)) {
                return [new GuestRegisterFail(err)];
              }
              return [
                new GuestRegisterFail(err),
                new ShowError(notificationConfig.guest.register.error)
              ];
            })
          );
      })
    )
  );
  guestCustomQuestionResponses$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadGuestCustomQuestionResponses>(
        LOAD_GUEST_CUSTOM_QUESTION_RESPONSES
      ),
      switchMap(({ token }) =>
        this.facade.loadCustomQuestionResponses(token).pipe(
          map(
            customQuestionResponses =>
              new LoadGuestCustomQuestionResponsesSuccess(
                customQuestionResponses
              )
          ),
          catchError(err => [
            new LoadGuestCustomQuestionResponsesFail(err),
            new ShowError(
              notificationConfig.guest.customQuestionResponses.error
            )
          ])
        )
      )
    )
  );
  branding$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadGuestBranding>(LOAD_GUEST_BRANDING),
      switchMap(({ token }) =>
        this.facade.loadBranding(token).pipe(
          map(branding => new LoadGuestBrandingSuccess(branding)),
          catchError(err => [
            new LoadGuestBrandingFail(err),
            new ShowError(notificationConfig.guest.branding.error)
          ])
        )
      )
    )
  );
  delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DeleteGuest>(DELETE_GUEST),
      switchMap(({ token, reasonData }) =>
        this.facade.delete(reasonData, token).pipe(
          mergeMap(() => [
            new DeleteGuestSuccess(),
            new ShowInfo(notificationConfig.guest.delete.success)
          ]),
          catchError(err => [
            new DeleteGuestFail(err),
            new ShowError(notificationConfig.guest.delete.error)
          ])
        )
      )
    )
  );
  loadGuestAppointments$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadGuestAppointments>(LOAD_GUEST_APPOINTMENTS),
      switchMap(({ token }) =>
        this.facade.loadGuestAppointments(token).pipe(
          mergeMap(appointmentsBundles => [
            new LoadGuestAppointmentsSuccess(appointmentsBundles)
          ]),
          catchError(err => [
            new LoadGuestAppointmentsFail(err),
            new ShowError(notificationConfig.guest.loadAppointment.error)
          ])
        )
      )
    )
  );
  loadGuestApplication$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadGuestApplication>(LOAD_GUEST_APPLICATION),
      switchMap(({ token }) =>
        this.facade.loadGuestApplication(token).pipe(
          mergeMap(application => [
            new LoadGuestApplicationSuccess(application)
          ]),
          catchError(err => [
            new LoadGuestApplicationFail(err),
            new ShowError(errorMessageParser(err))
          ])
        )
      )
    )
  );
  loadGuestQuestions$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadGuestHierarchicalQuestionsResponses>(
        LOAD_GUEST_HIERARCHICAL_QUESTION_RESPONSES
      ),
      switchMap(({ token }) =>
        this.facade.loadGuestQuestions(token).pipe(
          mergeMap(application => [
            new LoadGuestHierarchicalQuestionsResponsesSuccess(application)
          ]),
          catchError(err => [
            new LoadGuestHierarchicalQuestionsResponsesFail(err),
            new ShowError(notificationConfig.guest.loadQuestionsResponses.error)
          ])
        )
      )
    )
  );
  acceptGuestAppointment$ = createEffect(() =>
    this.actions$.pipe(
      ofType<AcceptGuestAppointment>(ACCEPT_GUEST_APPOINTMENT),
      switchMap(({ appointmentId, token }) =>
        this.facade.acceptAppointment(appointmentId, token).pipe(
          mergeMap(() => [
            new AcceptGuestAppointmentSuccess(),
            new ShowInfo(notificationConfig.guest.acceptAppointment.success)
          ]),
          catchError(err => [
            new AcceptGuestAppointmentFail(err),
            new ShowError(notificationConfig.guest.acceptAppointment.error)
          ])
        )
      )
    )
  );
  cancelGuestAppointment$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CancelGuestAppointment>(CANCEL_GUEST_APPOINTMENT),
      switchMap(({ cancelAppointmentData, token }) =>
        this.facade.cancelAppointment(cancelAppointmentData, token).pipe(
          mergeMap(() => [
            new CancelGuestAppointmentSuccess(),
            new ShowInfo(notificationConfig.guest.cancelAppointment.success)
          ]),
          catchError(err => [
            new CancelGuestAppointmentFail(err),
            new ShowError(notificationConfig.guest.cancelAppointment.error)
          ])
        )
      )
    )
  );
  guestDeclareIntent$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GuestDeclareIntent>(GUEST_DECLARE_INTENT),
      switchMap(({ declareIntentData, token }) =>
        this.facade.declareIntent(declareIntentData, token).pipe(
          mergeMap(() => [
            new GuestDeclareIntentSuccess(),
            new ShowInfo(notificationConfig.guest.declareIntent.success)
          ]),
          catchError(err => [
            new GuestDeclareIntentFail(err),
            new ShowError(notificationConfig.guest.declareIntent.error)
          ])
        )
      )
    )
  );
  private modalService = inject(ModalService);
  private store = inject(Store);
  openGuestRegisterModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<OpenGuestRegisterModal>(OPEN_GUEST_REGISTER_MODAL),
        tap(({ application, token, declareIntent }) => {
          this.modalService
            .open(GuestRegisterModalComponent, {
              backdrop: 'static',
              keyboard: false,
              data: {
                email: application.userProfile.email,
                declareIntent,
                captchaSettings: application.captchaSettings
              }
            })
            .onClose()
            .subscribe(registrationData => {
              const tenantRegisterInput =
                this.getTenantRegisterInput(application);
              tenantRegisterInput.password = registrationData.password;
              tenantRegisterInput.confirmPassword = registrationData.password;
              this.store.dispatch(
                new GuestRegister(
                  tenantRegisterInput,
                  token,
                  declareIntent ? application.id : undefined,
                  registrationData.captcha || undefined
                )
              );
            });
        })
      ),
    { dispatch: false }
  );
  private sessionStorage = inject(SessionStorageService);
  private captcha = inject(CaptchaRejectionService);

  private registerSuccess(userData: UserData, applicationId?: string) {
    return [
      new ShowInfo(notificationConfig.guest.register.success),
      new Go({
        path: [MainPageNavigation.EMAIL_VERIFICATION_PENDING],
        query: {
          justRegistered: true,
          applicationId
        },
        extras: {
          skipLocationChange: true,
          replaceUrl: true
        }
      })
    ];
  }

  private getTenantRegisterInput(application: GuestApplication) {
    const { userProfile } = application;
    const { email, data, address } = userProfile;
    const { firstname, name, phone } = data;
    return {
      firstName: firstname,
      lastName: name,
      email,
      password: null,
      confirmPassword: null,
      phone,
      socialLogin: false,
      address,
      searchProfile: null,
      customQuestionResponses: [],
      questionResponses: application.questionResponses
    };
  }
}
